import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthContext } from '@/context/AuthContext';
import { UserType } from '@/types/auth';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedUserTypes?: UserType[];
  requireAuth?: boolean;
}

const ProtectedRoute = ({ 
  children, 
  allowedUserTypes = [], 
  requireAuth = true 
}: ProtectedRouteProps) => {
  const { 
    session, 
    user, 
    userType, 
    isLoading, 
    userLoading
  } = useAuthContext();


  if (isLoading || userLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }


  if (requireAuth && !session) {
    return <Navigate to="/login" replace />;
  }


  if (requireAuth && session && !user) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading user data...</p>
        </div>
      </div>
    );
  }


  if (allowedUserTypes.length > 0 && userType && userType !== 'superadmin' && !allowedUserTypes.includes(userType)) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️ Access Denied</div>
          <p className="text-gray-600 mb-4">
            This page is not available for your account type.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Required: {allowedUserTypes.join(' or ')} | Your type: {userType}
          </p>
          <button 
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;